/** FORMULAS.md 用: 見出しパース（ページと Vitest で共有） */

export type FormulaTocItem = { id: string; title: string; level: number };

export type FormulaContentLine =
  | { kind: 'heading'; id: string; text: string; level: number }
  | { kind: 'text'; text: string };

const MARKDOWN_HEADING_LINE = /^(#{1,3})\s+(.+)$/;

function slugifyForId(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\u0080-\uffff-]/gi, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return s || 'section';
}

function stripLinkTitle(title: string): string {
  return title.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').trim();
}

/** 見出しタイトルから TOC／本文で共有する id（同一見出しの出現回数でサフィックス） */
function allocateHeadingId(title: string, slugCounts: Map<string, number>): string {
  const base = slugifyForId(title);
  const n = (slugCounts.get(base) ?? 0) + 1;
  slugCounts.set(base, n);
  return n === 1 ? base : `${base}-${n}`;
}

/** 1 回の走査で目次と本文行を構築（ページはこちらを優先して使う） */
export function buildFormulaTocAndContent(md: string): {
  toc: FormulaTocItem[];
  contentLines: FormulaContentLine[];
} {
  const lines = md.split('\n');
  const slugCounts = new Map<string, number>();
  const toc: FormulaTocItem[] = [];
  const contentLines: FormulaContentLine[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    const m = MARKDOWN_HEADING_LINE.exec(trimmed);
    if (m) {
      const level = m[1].length;
      const title = stripLinkTitle(m[2]);
      const id = allocateHeadingId(title, slugCounts);
      toc.push({ id, title, level });
      contentLines.push({ kind: 'heading', id, text: line, level });
    } else {
      contentLines.push({ kind: 'text', text: line });
    }
  }
  return { toc, contentLines };
}

export function buildFormulaToc(md: string): FormulaTocItem[] {
  return buildFormulaTocAndContent(md).toc;
}

export function buildFormulaContentLines(md: string): FormulaContentLine[] {
  return buildFormulaTocAndContent(md).contentLines;
}
